<template>
    <div class="quiz-container">
      <h2 class="quiz-title">{{ quizTitle }}</h2>
      <div v-if="!showSummary">
    
        <p class="question">{{ currentQuestion.beforeText }} <strong>_______</strong> {{ currentQuestion.afterText }}</p>
        <div class="options-container">
          <button
            v-for="option in currentQuestion.options"
            :key="option"
            :class="{'option-button': true, 'correct': option === currentQuestion.correctAnswer && feedbackVisible, 'incorrect': option !== currentQuestion.correctAnswer && option === selectedOption && feedbackVisible}"
            @click="selectOption(option)"
          >
            {{ option }}
          </button>
      
        </div>
        <audio ref="correctSound" src="https://assets.codepen.io/11151897/nyse.m4a"></audio>
        <div class="feedback" v-if="feedbackVisible">
          <span :class="{ 'correct': isAnswerCorrect, 'incorrect': !isAnswerCorrect }">{{ feedbackMessage }}</span>
        </div>
      </div><br>
      <button class="hint-button" @click="openHint(currentQuestion.hint)">
      <i class="fa fa-paw"></i> Hint
    </button>
      <div v-if="showSummary" class="summary-container">
        <h3>Quiz Summary</h3>
        <p>Correct Answers: {{ correctAnswers }} out of {{ totalQuestions }}</p>
        <button @click="restartQuiz" class="restart-quiz-button">Restart Quiz</button>
        <button @click="goToNextQuiz" class="restart-quiz-button">Next Quiz</button>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed } from 'vue';
  import Swal from 'sweetalert2';
  
  export default {
    setup() {
      const correctSound = ref(null);
      const quizTitle = "Risk Management";
      const questions = ref([
        {
          beforeText: "The process of spreading investments across various financial assets to reduce risk is known as",
          afterText: ".",
          options: ["Diversification", "Allocation", "Hedging", "Speculation"],
          correctAnswer: "Diversification",
          hint: {
          text: "Think of it like having a number of different breed cats.  You'll spread across the risk that they are all crazy (but adorable) like orange cats",
          imageUrl: require('@/assets/basketofkittens.png')
        }
        },
        {
          beforeText: "A legally binding document that guarantees the lender interest payments and the return of principal is called a",
          afterText: ".",
          options: ["Stock", "Bond", "Derivative", "Mutual Fund"],
          correctAnswer: "Bond",
          hint: {
          text: "Think of being a foster parent for a kitten.  You get to enjoy the cuddles and love which is the perfect form of payment but eventually will give it back as you are not an answer of the kitten rather just a borrower.",
          imageUrl: require('@/assets/basketofkittens.png')
        }
        },
        {
          beforeText: "The market condition characterized by rising stock prices is referred to as a",
          afterText: "market.",
          options: ["Bull", "Bear", "Stagnant", "Volatile"],
          correctAnswer: "Bull",
          hint: {
          text: "Your hint for the first question",
          imageUrl: require('@/assets/basketofkittens.png')
        }
        },
        {
          beforeText: "The financial metric that measures the profitability of an investment as a percentage of the original amount is known as",
          afterText: ".",
          options: ["ROI", "EPS", "P/E Ratio", "Dividend Yield"],
          correctAnswer: "ROI",
          hint: {
          text: "Your hint for the first question",
          imageUrl: require('@/assets/basketofkittens.png')
        }
        },
        {
          beforeText: "The principle of not putting all your eggs in one basket to manage risk in investing is known as",
          afterText: ".",
          options: ["Diversification", "Arbitrage", "Leveraging", "Market Timing"],
          correctAnswer: "Diversification",
          hint: {
          text: "Your hint for the first question",
          imageUrl: require('@/assets/basketofkittens.png')
        }
        },
        
      ]);
      const currentQuestionIndex = ref(0);
    const correctAnswers = ref(0);
    const feedbackMessage = ref("");
    const feedbackVisible = ref(false);
    const isAnswerCorrect = ref(false);
    const showSummary = ref(false);
    const selectedOption = ref("");
    const totalQuestions = computed(() => questions.value.length);
    const openHint = (hint) => {
      Swal.fire({
        title: 'Hint',
        text: hint.text,
        imageUrl: hint.imageUrl,
        imageWidth: 400,
        imageHeight: 200,
        imageAlt: 'A hint image',
      });
    };

    const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);

    const selectOption = (option) => {
  selectedOption.value = option;
  isAnswerCorrect.value = option === currentQuestion.value.correctAnswer;
  feedbackMessage.value = isAnswerCorrect.value ? "Correct!" : "Incorrect!";
  feedbackVisible.value = true;

  // Only play the sound if the answer is correct
  if (isAnswerCorrect.value) {
    correctAnswers.value++;
    if (correctSound.value) {
      correctSound.value.play();
    }
  }
      setTimeout(() => {
        if (currentQuestionIndex.value < questions.value.length - 1) {
          currentQuestionIndex.value++;
          feedbackVisible.value = false;
        } else {
          showSummary.value = true;
        }
      }, 2000);
    };

    const restartQuiz = () => {
      currentQuestionIndex.value = 0;
      correctAnswers.value = 0;
      feedbackVisible.value = false;
      showSummary.value = false;
    };

    return {
      quizTitle,
      currentQuestion,
      correctAnswers,
      selectOption,
      restartQuiz,
      showSummary,
      feedbackMessage,
      feedbackVisible,
      isAnswerCorrect,
      correctSound,
      selectedOption,
      totalQuestions,
      openHint,
    };
  },
};
</script>



<style scoped>
.quiz-container {
  background-color: #4A148C; /* Light purple background */
  color: #FFF; /* Dark font color for contrast */
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: 20px auto; /* Center container and give some space around */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Soft shadow for depth */
}

.question-container {
  margin-bottom: 20px; /* Space below the question for options */
}

.question {
  font-size: 1.25rem; /* Larger font size for readability */
  color: #fff; /* Dark purple for emphasis */
  margin-bottom: 15px; /* Space between question and options */
}

.options-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two options per row */
  gap: 10px; /* Space between options */
}

.option-button {
  padding: 10px 20px;
  border-radius: 25px;
  border: 2px solid #4A148C; /* Dark purple border */
  background-color: #f4f4f8; /* Light background for options */
  color: #36454f; /* Dark font color for contrast */
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.3s, background-color 0.3s;
}

.option-button:hover {
  background-color: #d0ccd3; /* Darker shade on hover */
  transform: translateY(-2px); /* Slight lift effect */
}

.feedback {
  margin-top: 20px;
  font-weight: bold;
}

.correct {
  color: #4CAF50; /* Green for correct answers */
}

.incorrect {
  color: #E53935; /* Red for incorrect answers */
}

.navigation-buttons {
  display: flex;
  margin-top: 20px;
}

.navigation-button {
  background-color: #36454f; 
  color: white;
  border: none;
  border-radius: 25px;
  padding: 10px 20px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.navigation-button:hover {
  background-color: #4A148C; 
}

.restart-quiz-button {
  background-color: #36454f; 
  color: white; 
  border: none; 
  padding: 10px 20px; 
  cursor: pointer; 
  border-radius: 25px; 
  font-weight: bold; 
  transition: background-color 0.3s, color 0.3s; /* Smooth transition for hover effects */
  margin-top: 20px; 
}

.restart-quiz-button:hover {
  background-color: #4A148C; 
}
.hint-button {
  background-color: #36454f; 
  justify-content: center;
  color: white; 
  border: none;
  border-radius: 5px; 
  padding: 5px 10px; 
  cursor: pointer; 
  transition: background-color 0.3s ease; 
  right: 10px;
}

.hint.button:hover {
  background-color: black; 
}

</style>